import { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { useTheme } from '@emotion/react'

import { StylesHeaderVariables } from '~partials/header/UI'
import Data from '~components/Data'

export const StylesHeroVariables = {
  compact: 350,
  small: 400,
  large: 700,
}

export const HeroTitle = forwardRef(
  ({ as: Component = 'h1', className, size = 'base', ...rest }, ref) => {
    const theme = useTheme()

    return (
      <Component
        ref={ref}
        css={[
          size === 'large' && {
            ...theme.text['5xlarge__fluid'],
            fontWeight: theme.fontWeight.xbold,
          },
        ]}
        className={clsx('Hero__Title', className)}
        {...rest}
      />
    )
  }
)

HeroTitle.propTypes = {
  as: PropTypes.any,
  className: PropTypes.string,
  size: PropTypes.oneOf(['base', 'large']),
}

export const HeroTitleMeta = ({ as: Component = 'h5', className, ...rest }) => {
  const theme = useTheme()

  return (
    <Component
      css={{
        maxWidth: 750,
        marginTop: theme.space.medium,
        marginLeft: 'auto',
        marginRight: 'auto',
        marginBottom: 0,
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '1fr auto 1fr',
        gap: theme.space.base,
        alignContent: 'center',

        '&::before, &::after': {
          content: "''",
          background: theme.contrast.base,
          height: 2,
          display: 'block',
          width: '100%',
          maxWidth: 100,
          alignSelf: 'center',
        },

        '&::before': {
          justifySelf: 'end',
        },
      }}
      className={clsx('Hero__TitleMeta', className)}
      {...rest}
    />
  )
}

HeroTitleMeta.propTypes = {
  as: PropTypes.oneOf(['h4', 'h5']),
  className: PropTypes.string,
}

export const HeroTitleSub = ({ className, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={{
        ...theme.text.medium,
        color: theme.fontColor.base,

        '.Hero__TitleWrapper &': {
          maxWidth: 750,
          margin: '0 auto',
        },

        '.u-contrast &': {
          color: theme.contrast.base,
        },
      }}
      className={clsx('Hero__TitleSub', className)}
      {...rest}
    />
  )
}

HeroTitleSub.propTypes = {
  className: PropTypes.string,
}

export const HeroContent = ({ className, ...rest }) => (
  <div
    css={{
      position: 'relative',
      zIndex: 2,
      width: '100%',
    }}
    className={clsx('Hero__Content', className)}
    {...rest}
  />
)

HeroContent.propTypes = {
  className: PropTypes.string,
}

export const HeroTitleWrapper = ({ className, bottomSpace, ...rest }) => {
  const theme = useTheme()

  return (
    <div
      css={[
        {
          textAlign: 'center',
        },

        bottomSpace && {
          marginBottom: theme.space.large,
        },
      ]}
      className={clsx(
        `Hero__TitleWrapper ${theme.settings.classes.trim}`,
        className
      )}
      {...rest}
    />
  )
}

HeroTitleWrapper.propTypes = {
  bottomSpace: PropTypes.bool,
  className: PropTypes.string,
}

export const HeroFormWrapper = (props) => {
  const theme = useTheme()

  return (
    <div
      id="form"
      css={{
        background: theme.color.light.base,
        borderRadius: theme.borderRadius.base,
        padding: theme.space.large,
        boxShadow: theme.boxShadow.large,
      }}
      {...props}
    />
  )
}

export const HeroFormTitle = ({ children, ...rest }) => {
  const theme = useTheme()

  if (!children) return null

  return (
    <h3
      css={[
        theme.fontSize.xlarge__fluid,
        {
          marginBottom: theme.space.large,
          textAlign: 'center',

          [theme.mq.medium]: {
            textAlign: 'left',
          },
        },
      ]}
      {...rest}
    >
      <Data>{children}</Data>
    </h3>
  )
}

HeroFormTitle.propTypes = {
  children: PropTypes.any,
}

const Hero = forwardRef(
  (
    {
      children,
      className,
      compact,
      contrast = true,
      topOffset = true,
      ...rest
    },
    ref
  ) => {
    const theme = useTheme()

    return (
      <section
        css={[
          {
            display: 'grid',
            alignItems: 'center',
            paddingTop: topOffset
              ? StylesHeaderVariables(theme).topOffset
              : theme.space.large,
            paddingBottom: theme.space.large,
            position: 'relative',
          },

          !compact && {
            minHeight: StylesHeroVariables.small,

            [theme.mq.medium]: {
              minHeight: StylesHeroVariables.large,
            },
          },

          compact && {
            minHeight: StylesHeroVariables.compact,
          },
        ]}
        className={clsx('Hero', className, {
          'u-contrast': contrast,
        })}
        ref={ref}
        {...rest}
      >
        {children}
      </section>
    )
  }
)

Hero.propTypes = {
  className: PropTypes.string,
  compact: PropTypes.bool,
  contrast: PropTypes.bool,
  children: PropTypes.node,
  topOffset: PropTypes.bool,
}

export default Hero
